import { useState } from "react";
import { Search, X, Download } from "lucide-react";
import { colors, fonts } from "../../styles/theme";

export default function TranscriptViewer({ transcript, meetingTitle }) {
  const [search, setSearch] = useState("");

  if (
    !transcript ||
    (!transcript.segments?.length && !transcript.phrases?.length)
  ) {
    return (
      <div
        style={{
          textAlign: "center",
          padding: 32,
          color: colors.textSecondary,
          fontSize: 13,
        }}
      >
        No transcript available
      </div>
    );
  }

  // Prefer phrases (detailed with timestamps) over segments (combined)
  const displaySegments = (transcript.phrases || transcript.segments || []).map(
    (s) => ({
      startTime: s.startSeconds ?? s.startTime ?? 0,
      speaker: s.speaker || "",
      text: s.text || "",
    }),
  );

  const filtered = search
    ? displaySegments.filter((s) =>
        s.text.toLowerCase().includes(search.toLowerCase()),
      )
    : displaySegments;

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${String(s).padStart(2, "0")}`;
  };

  const exportTranscript = () => {
    const lines = displaySegments.map((seg) => {
      const time = formatTime(seg.startTime);
      const speaker = seg.speaker ? `${seg.speaker}: ` : "";
      return `[${time}] ${speaker}${seg.text}`;
    });
    const filename = meetingTitle
      ? `${meetingTitle.replace(/[^a-zA-Z0-9 _-]/g, "").slice(0, 60)} - Transcript.txt`
      : "transcript.txt";
    const blob = new Blob([lines.join("\n\n")], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const highlightMatch = (text) => {
    if (!search) return text;
    const idx = text.toLowerCase().indexOf(search.toLowerCase());
    if (idx === -1) return text;
    return (
      <>
        {text.slice(0, idx)}
        <mark
          style={{
            background: "rgba(245,158,11,0.3)",
            color: colors.text,
            borderRadius: 2,
            padding: "0 1px",
          }}
        >
          {text.slice(idx, idx + search.length)}
        </mark>
        {text.slice(idx + search.length)}
      </>
    );
  };

  return (
    <div>
      {/* Search bar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          background: colors.bgCard,
          border: `1px solid ${colors.border}`,
          borderRadius: 8,
          padding: "6px 12px",
          marginBottom: 12,
        }}
      >
        <Search size={14} color={colors.textSecondary} />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search transcript..."
          style={{
            background: "none",
            border: "none",
            outline: "none",
            color: colors.text,
            fontSize: 12,
            fontFamily: fonts.body,
            width: "100%",
          }}
        />
        {search && (
          <X
            size={12}
            color={colors.textSecondary}
            style={{ cursor: "pointer" }}
            onClick={() => setSearch("")}
          />
        )}
      </div>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <div style={{ fontSize: 11, color: colors.textSecondary }}>
          {filtered.length} segment{filtered.length !== 1 ? "s" : ""}
          {search && ` matching "${search}"`}
        </div>
        <button
          onClick={exportTranscript}
          title="Export transcript as text file"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 4,
            padding: "4px 10px",
            border: `1px solid ${colors.border}`,
            borderRadius: 6,
            background: "transparent",
            color: colors.textSecondary,
            fontSize: 11,
            fontFamily: fonts.body,
            cursor: "pointer",
          }}
        >
          <Download size={12} /> Export
        </button>
      </div>

      {/* Segments */}
      <div style={{ maxHeight: 400, overflowY: "auto" }}>
        {filtered.map((seg, i) => (
          <div
            key={i}
            style={{
              display: "flex",
              gap: 10,
              padding: "8px 0",
              borderBottom: `1px solid ${colors.borderLight}`,
            }}
          >
            <span
              style={{
                fontSize: 11,
                fontFamily: fonts.mono,
                color: colors.textSecondary,
                minWidth: 44,
                flexShrink: 0,
                paddingTop: 2,
              }}
            >
              {formatTime(seg.startTime)}
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              {seg.speaker && (
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: colors.accent,
                    marginRight: 6,
                  }}
                >
                  {seg.speaker}
                </span>
              )}
              <span
                style={{ fontSize: 13, color: colors.text, lineHeight: 1.5 }}
              >
                {highlightMatch(seg.text)}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
